import React, { useState, useEffect, useMemo } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTranslation } from "../../i18n/useTranslation";

const qKey = (id) => String(id ?? "");

function normalizeDiff(d, dl, t) {
  const s = String(d || "").toLowerCase();
  if (s.includes("easy")) return t("Easy");
  if (s.includes("medium")) return t("Medium");
  if (s.includes("difficult") || s.includes("hard")) return t("Hard");
  const n = Number(dl);
  if (n === 1) return t("Easy");
  if (n === 2) return t("Medium");
  if (n === 3) return t("Hard");
  return "";
}

function getQuestionTypeLabel(qt, t) {
  // Numeric codes from curriculum DB: 1=Fill-in-Blank, 2=MCQ, 3=Fill-in-Blank, 4=True/False, 7=Paragraph
  if (typeof qt === "number" || (typeof qt === "string" && /^\d+$/.test(qt))) {
    const byCode = {
      1: t("Fill-in-Blank"),
      2: t("MCQ"),
      3: t("Fill-in-Blank"),
      4: t("True/False"),
      7: t("Paragraph"),
      8: t("Paragraph"),
    };
    return byCode[Number(qt)] || t("Question");
  }
  const s = String(qt || "").toLowerCase();
  if (s.includes("mcq") || s.includes("multiple")) return t("MCQ");
  if (s.includes("fill")) return t("Fill-in-Blank");
  if (s.includes("true") || s.includes("tf")) return t("True/False");
  if (s.includes("short")) return t("Short Essay");
  if (s.includes("long")) return t("Long Essay");
  if (s.includes("assoc") || s.includes("word")) return t("Word Assoc.");
  return s || t("Question");
}

export default function WizardStep3Review({
  isDarkMode,
  questions = [],
  selectedQuestionIds,
  testDetails,
  pointValues = {},
  onUpdatePoints,
  visibility = "private",
  onUpdateVisibility,
  sharedClasses = [],
  onShareClass,
  onRemoveClass,
  teacherClasses = [],
  classesLoading = false,
  classesError = false,
  shareEmails = "",
  onUpdateShareEmails,
  onSaveDraft,
  onNext,
  onBack,
  saving = false,
}) {
  const c = getColors(isDarkMode);
  const { t } = useTranslation();

  // Selected questions only
  const selectedQuestions = useMemo(
    () => questions.filter((q) => selectedQuestionIds.has(qKey(q.question_id))),
    [questions, selectedQuestionIds]
  );

  // BDD 08/25/2026 - support decimal point values / official exam marks
  const totalPoints = useMemo(() => {
    return selectedQuestions.reduce((sum, q) => {
      const pts = Number(
        pointValues[qKey(q.question_id)] ??
        q.official_points ??
        q.score ??
        5
      );

      return sum + (Number.isFinite(pts) ? pts : 0);
    }, 0);
  }, [selectedQuestions, pointValues]);

  return (
    <View>
      {/* Header */}
      <View style={styles.header}>
        <Text style={[styles.heading, { color: c.textPrimary }]}>{t("Review & Configure")}</Text>
        <Text style={[styles.subheading, { color: c.textSecondary }]}>
          {t("Set point values, reorder questions, and configure test visibility and sharing.")}
        </Text>
      </View>

      <View style={styles.reviewGrid}>
        {/* Left - Questions */}
        <View style={styles.reviewLeft}>
          <View style={styles.reviewLeftHeader}>
            <Text style={[styles.reviewLeftTitle, { color: c.textPrimary }]}>
              {t("{count} Questions \u00B7 Total: {points} pts", {
                count: selectedQuestions.length,
                points: totalPoints,
              })}
            </Text>
          </View>

          {selectedQuestions.map((q, idx) => {
            const key = qKey(q.question_id);
            const pts = pointValues[key] ?? q.score ?? 5;
            const typeLabel = getQuestionTypeLabel(q.question_type, t);
            const diff = normalizeDiff(q.difficulty, q.difficulty_level, t);
            const questionText =
              typeof q.question === "string"
                ? q.question
                : q.question?.text || q.question_text || "";

            return (
              <View
                key={key}
                style={[styles.reviewQ, { backgroundColor: c.cardBg, borderColor: c.border }]}
              >
                {/* Number */}
                <View style={[styles.qNum, { backgroundColor: c.accentLight }]}>
                  <Text style={[styles.qNumText, { color: c.accent }]}>{idx + 1}</Text>
                </View>

                {/* Content */}
                <View style={styles.qContent}>
                  <Text style={[styles.qMeta, { color: c.accent }]}>
                    {typeLabel}
                    {diff ? ` \u00B7 ${diff}` : ""}
                  </Text>
                  <Text style={[styles.qText, { color: c.textPrimary }]} numberOfLines={2}>
                    {questionText}
                  </Text>
                </View>

                {/* Points input */}
                <View style={styles.qPoints}>
                  <TextInput
                    style={[
                      styles.pointsInput,
                      { color: c.textPrimary, backgroundColor: c.inputBg, borderColor: c.border },
                    ]}
                    value={String(pts)}
                    onChangeText={(v) => onUpdatePoints(key, v)}
                    keyboardType="numeric"
                  />
                  <Text style={[styles.ptsLabel, { color: c.textMuted }]}>{t("pts")}</Text>
                </View>
              </View>
            );
          })}

          {selectedQuestions.length === 0 && (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons name="clipboard-alert-outline" size={48} color={c.textMuted} />
              <Text style={[styles.emptyText, { color: c.textMuted }]}>
                {t("No questions selected. Go back to add questions.")}
              </Text>
            </View>
          )}
        </View>

        {/* Right - Config Panel */}
        <View style={[styles.configPanel, { backgroundColor: c.cardBg, borderColor: c.border }]}>
          {/* Visibility */}
          <View style={styles.configSection}>
            <Text style={[styles.configTitle, { color: c.textPrimary }]}>{t("Visibility")}</Text>
            <View style={styles.visOptions}>
              {[
                { key: "private", label: t("Private"), desc: t("Only shared students can access") },
                { key: "organization", label: t("Organization"), desc: t("All teachers in your org") },
                { key: "public", label: t("Public"), desc: t("Anyone with the link") },
              ].map((opt) => {
                const isSelected = visibility === opt.key;
                return (
                  <TouchableOpacity
                    key={opt.key}
                    style={[
                      styles.visOption,
                      {
                        borderColor: isSelected ? c.accent : c.border,
                        backgroundColor: isSelected ? c.accentLight : "transparent",
                      },
                    ]}
                    onPress={() => onUpdateVisibility(opt.key)}
                    activeOpacity={0.7}
                  >
                    <View
                      style={[
                        styles.visRadio,
                        { borderColor: isSelected ? c.accent : c.border },
                      ]}
                    >
                      {isSelected && (
                        <View style={[styles.visRadioDot, { backgroundColor: c.accent }]} />
                      )}
                    </View>
                    <View>
                      <Text style={[styles.visLabel, { color: c.textPrimary }]}>{opt.label}</Text>
                      <Text style={[styles.visDesc, { color: c.textMuted }]}>{opt.desc}</Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {/* Share with Classes */}
          <View style={[styles.configSection, { borderTopColor: c.borderLight }]}>
            <Text style={[styles.configTitle, { color: c.textPrimary }]}>{t("Share with Classes")}</Text>
            <Text style={[styles.classHint, { color: c.textMuted }]}>
              {t("Tap a class to share this test with it")}
            </Text>
            {classesLoading ? (
              <Text style={[styles.classStatus, { color: c.textMuted }]}>{t("Loading classes…")}</Text>
            ) : classesError ? (
              <Text style={[styles.classStatus, { color: c.textMuted }]}>
                {t("Couldn't load your classes. Try again later.")}
              </Text>
            ) : teacherClasses.length === 0 ? (
              <Text style={[styles.classStatus, { color: c.textMuted }]}>
                {t("No classes yet. Create a class in Manage Students to share this test with it.")}
              </Text>
            ) : null}
            <View style={styles.classChips}>
              {teacherClasses.map((cl) => {
                const isShared = sharedClasses.some(
                  (sc) => String(sc.id || sc.class_id) === String(cl.id)
                );
                return (
                  <TouchableOpacity
                    key={cl.id}
                    style={[
                      styles.classChip,
                      {
                        backgroundColor: isShared ? c.accentLight : "transparent",
                        borderColor: isShared ? c.accent : c.border,
                      },
                    ]}
                    onPress={() => {
                      if (isShared) {
                        onRemoveClass && onRemoveClass(cl.id);
                      } else {
                        onShareClass && onShareClass(cl.id);
                      }
                    }}
                    activeOpacity={0.7}
                  >
                    <Text
                      style={[
                        styles.classChipText,
                        { color: isShared ? c.accentText : c.textSecondary },
                      ]}
                    >
                      {cl.name}
                    </Text>
                    {isShared && (
                      <MaterialCommunityIcons name="close" size={12} color={c.accentText} />
                    )}
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {/* Share with Students */}
          <View style={[styles.configSection, { borderTopColor: c.borderLight }]}>
            <Text style={[styles.configTitle, { color: c.textPrimary }]}>{t("Share with Students")}</Text>
            <TextInput
              style={[
                styles.input,
                { color: c.textPrimary, backgroundColor: c.inputBg, borderColor: c.border, fontSize: 13, marginBottom: 4 },
              ]}
              placeholder={t("Enter student emails...")}
              placeholderTextColor={c.textMuted}
              value={shareEmails}
              onChangeText={onUpdateShareEmails}
            />
            <Text style={[styles.shareHint, { color: c.textMuted }]}>
              {t("Separate multiple emails with commas")}
            </Text>
          </View>

          {/* Step Actions — kept inside the right config panel so they remain
              visible without scrolling past a long questions list. */}
          <View style={[styles.panelActions, { borderTopColor: c.borderLight, borderBottomWidth: 0 }]}>
            <TouchableOpacity
              style={[styles.btnPrimaryFull, { backgroundColor: c.accent }]}
              onPress={onNext}
              activeOpacity={0.7}
            >
              <Text style={styles.btnPrimaryText}>{t("Continue to Publish")}</Text>
              <MaterialCommunityIcons name="chevron-right" size={16} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.btnSecondaryFull, { borderColor: c.border, backgroundColor: c.cardBg }]}
              onPress={onSaveDraft}
              activeOpacity={0.7}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator size="small" color={c.textPrimary} />
              ) : (
                <Text style={[styles.btnSecondaryText, { color: c.textPrimary }]}>{t("Save as Draft")}</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.btnSecondaryFull, { borderColor: c.border, backgroundColor: c.cardBg }]}
              onPress={onBack}
              activeOpacity={0.7}
            >
              <MaterialCommunityIcons name="chevron-left" size={16} color={c.textPrimary} />
              <Text style={[styles.btnSecondaryText, { color: c.textPrimary }]}>{t("Back")}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
}

function getColors(isDarkMode) {
  if (isDarkMode) {
    return {
      textPrimary: "#f1f5f9",
      textSecondary: "#94a3b8",
      textMuted: "#64748b",
      border: "#334155",
      borderLight: "#1e293b",
      cardBg: "#1e293b",
      inputBg: "#1e293b",
      accent: "#818cf8",
      accentLight: "rgba(99,102,241,0.12)",
      accentText: "#a5b4fc",
      teal: "#2dd4bf",
    };
  }
  return {
    textPrimary: "#0f172a",
    textSecondary: "#475569",
    textMuted: "#94a3b8",
    border: "#e2e8f0",
    borderLight: "#f1f5f9",
    cardBg: "#ffffff",
    inputBg: "#f8fafc",
    accent: "#6366f1",
    accentLight: "#eef2ff",
    accentText: "#4338ca",
    teal: "#0d9488",
  };
}

const styles = StyleSheet.create({
  header: { marginBottom: 24 },
  heading: { fontSize: 24, fontWeight: "400", marginBottom: 6 },
  subheading: { fontSize: 14, lineHeight: 21 },

  /* Review grid */
  reviewGrid: { flexDirection: "row", gap: 24 },
  reviewLeft: { flex: 1, minWidth: 0 },
  reviewLeftHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 14,
  },
  reviewLeftTitle: { fontSize: 14, fontWeight: "700" },

  /* Review question card */
  reviewQ: {
    flexDirection: "row",
    gap: 14,
    alignItems: "flex-start",
    padding: 16,
    borderWidth: 1,
    borderRadius: 10,
    marginBottom: 10,
  },
  qNum: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  qNumText: { fontSize: 13, fontWeight: "700" },
  qContent: { flex: 1 },
  qMeta: { fontSize: 11, fontWeight: "600", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 },
  qText: { fontSize: 14, lineHeight: 21 },
  qPoints: { flexDirection: "row", alignItems: "center", gap: 6, flexShrink: 0 },
  pointsInput: {
    width: 50,
    textAlign: "center",
    paddingVertical: 5,
    paddingHorizontal: 4,
    borderWidth: 1,
    borderRadius: 6,
    fontSize: 13,
    fontWeight: "600",
  },
  ptsLabel: { fontSize: 12 },

  /* Config panel */
  configPanel: {
    width: 340,
    borderWidth: 1,
    borderRadius: 16,
    padding: 22,
    alignSelf: "flex-start",
  },
  configSection: {
    marginBottom: 20,
    paddingBottom: 16,
    borderTopWidth: 0,
  },
  configTitle: { fontSize: 15, fontWeight: "700", marginBottom: 16 },

  /* Visibility options */
  visOptions: { gap: 8 },
  visOption: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    padding: 10,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderRadius: 6,
  },
  visRadio: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  visRadioDot: { width: 10, height: 10, borderRadius: 5 },
  visLabel: { fontSize: 13, fontWeight: "600" },
  visDesc: { fontSize: 11 },

  /* Class chips */
  classHint: { fontSize: 12, marginBottom: 8 },
  classStatus: { fontSize: 13, marginBottom: 8 },
  classChips: { flexDirection: "row", flexWrap: "wrap", gap: 6 },
  classChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 20,
    borderWidth: 1,
  },
  classChipText: { fontSize: 12, fontWeight: "600" },

  /* Share hint */
  shareHint: { fontSize: 11 },

  /* Inputs */
  input: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderRadius: 6,
    fontSize: 14,
  },

  /* Empty state */
  emptyState: { alignItems: "center", paddingVertical: 60, gap: 12 },
  emptyText: { fontSize: 14, textAlign: "center" },

  /* Panel actions — vertically stacked inside the right config panel */
  panelActions: {
    paddingTop: 16,
    marginTop: 4,
    borderTopWidth: 1,
    gap: 10,
  },
  btnPrimaryFull: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
    width: "100%",
    shadowColor: "rgba(99,102,241,0.25)",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 3,
  },
  btnSecondaryFull: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 1,
    width: "100%",
  },
  btnPrimaryText: { color: "#fff", fontSize: 14, fontWeight: "600" },
  btnSecondaryText: { fontSize: 14, fontWeight: "600" },
});
